import { type EntityState } from '@ngrx/entity';
import { type Draft } from 'immer';

import {
  type CanvasElement,
  type CanvasElementFrom,
  type CanvasElementId,
  type CanvasElementType,
  type PropsOf,
} from '@blaze-runtime/engine-domain';
import { applyPropsPatch } from '@blaze-runtime/engine-domain';
import { type DataBindingSourceInfo, SearchAndFiltersMode } from '@blaze-shared/elements-domain';
import { findColumn } from '@blaze-shared/schema-domain';
import { regexpGetObjectKey } from '@blaze-shared/shared-util';
import {
  type AggregateWorkflowNode,
  findWorkflowNode,
  isAggregateNode,
  isNodeIdWithChartsComputePrefix,
  type Workflow,
  type WorkflowNode,
} from '@blaze-shared/workflow-domain';

import {
  areContentPropsWithTitle,
  hasProps,
} from '../../../canvas-elements/element-model/element-props-guards';
import {
  isElementType,
  isVariantOfChart,
} from '../../../canvas-elements/element-model/element-type-guards';

/** List of elements which should have the label automatically applied */
const ELEMENTS_WITH_AUTO_INITIAL_LABEL: CanvasElementType[] = [
  'editableList',
  'formDetailsList',
  'formContainer',
  'formDetails',
  'ganttChart',
  'list',
  'scheduler',
  'table',
];

export function updateElementAfterSourceSelectionMutably(
  draft: Draft<EntityState<CanvasElement>>,
  elementId: CanvasElementId,
  selectedDataSource: DataBindingSourceInfo,
  isInitialSelection: boolean,
  dataSourceName?: string,
  applyDynamicSearch?: boolean | null,
): void {
  const element = draft.entities[elementId];
  if (!element || !hasProps(element)) {
    return;
  }

  if (isVariantOfChart(element) && element.props.content?.data && !isInitialSelection) {
    const chartProps = element.props.content.data;
    const keys: (keyof typeof chartProps)[] = [
      'mainValue',
      'compareValue',
      'groupBy',
      'groupByDetails',
      'segmentData',
    ];
    // if any key doesn't exist in selected datasource, remove it from chart props
    const mismatchedKey = keys.find(
      (key) =>
        chartProps[key] &&
        !findColumn(selectedDataSource.columns, chartProps[key] as string | undefined),
    );
    if (mismatchedKey) {
      (
        [
          'aggregation',
          'compareAggregation',
          'groupByDetails',
          'segmentDataDetails',
          ...keys,
        ] as (keyof typeof chartProps)[]
      ).forEach((prop) => {
        if (prop in chartProps) {
          chartProps[prop] = undefined;
        }
      });
    }
  }

  if (isElementType(element, 'table') && element.props.content.rowClick?.enabled) {
    element.props.content.rowClick.enabled = false;
  }

  if (isElementType(element, 'dropdown') && applyDynamicSearch === null) {
    element.props = applyPropsPatch(element.props, {
      content: { menu: { dynamicSearch: null } },
    }) as typeof element.props;
  }

  if (
    isInitialSelection &&
    ((isElementType(element, 'table') && selectedDataSource.sourceType === 'dataset') ||
      isElementType(element, 'dataFilter'))
  ) {
    element.props = applyPropsPatch(element.props, {
      content: {
        searchAndFilters: {
          mode: SearchAndFiltersMode.PREDEFINED,
          searchEnabled: true,
        },
      },
    }) as typeof element.props;
  }

  tryApplyLabelMutably(draft, elementId, isInitialSelection, dataSourceName);
}

const FORMULA_TO_DETAILS_MAP: [string, string][] = [
  ['DAY(', 'daily'],
  ['WEEKSTARTDATE(', 'weekly'],
  ['QUARTER(', 'quarterly'],
  ['MONTH(', 'monthly'],
  ['YEAR(', 'yearly'],
];
function extractFormulaDetails(
  computeNodesFormulas: string[],
  node: AggregateWorkflowNode,
): {
  data?: string;
  details?: string;
} {
  if (computeNodesFormulas.length) {
    return {
      data: regexpGetObjectKey(computeNodesFormulas[0]),
      details: FORMULA_TO_DETAILS_MAP.find(([key]) =>
        computeNodesFormulas.some((f) => f.includes(key)),
      )?.[1],
    };
  }

  return {
    data: node.groupByColumns[0],
    details: undefined,
  };
}
function getChartComputeNodesFormulas(nodes: WorkflowNode[]): string[] {
  return nodes
    .filter(isNodeIdWithChartsComputePrefix)
    .map((node) => node.formula)
    .filter((s): s is string => !!s);
}

function updateChartSinglePropsMutably(
  element: Draft<CanvasElementFrom<'chartSingle'>>,
  workflow: Workflow,
): void {
  const node = findWorkflowNode(workflow.nodes, isAggregateNode, 'aggregate', 'chart-');
  element.props = applyPropsPatch(element.props, {
    content: {
      data: {
        aggregation: node?.functionDefs[0]?.name,
        mainValue: node?.functionDefs[0]?.column,
        alias: node?.functionDefs[0]?.alias,
      },
    },
  }) as PropsOf<'chartSingle'>;
}
function updateSweetChartPropsMutably(
  element: Draft<CanvasElementFrom<'chartPie' | 'chartDoughnut'>>,
  workflow: Workflow,
): void {
  const node = findWorkflowNode(workflow.nodes, isAggregateNode, 'aggregate', 'chart-');
  if (!node?.functionDefs[0]) {
    console.error(new Error('No aggregate node found in workflow for chart element'));
    return;
  }
  // When data are segmented by date column we need to get column and period
  const computeData = extractFormulaDetails(getChartComputeNodesFormulas(workflow.nodes), node);

  element.props = applyPropsPatch(element.props, {
    content: {
      data: {
        aggregation: node.functionDefs[0].name,
        mainValue: node.functionDefs[0].column,
        alias: node.functionDefs[0].alias,
        segmentData: computeData.data,
        ...(computeData.details && { segmentDataDetails: computeData.details }),
      },
    },
  }) as PropsOf<'chartPie' | 'chartDoughnut'>;
}
function updateChartPropsMutably(
  element: Draft<CanvasElementFrom<'chartBar' | 'chartLine' | 'chartColumn' | 'chartDot'>>,
  workflow: Workflow,
): void {
  const node = findWorkflowNode(workflow.nodes, isAggregateNode, 'aggregate', 'chart-');
  if (!node?.functionDefs[0]) {
    console.error(new Error('No aggregate node found in workflow for chart element'));
    return;
  }
  // When data are segmented by date column we need to get column and period
  const computeData = extractFormulaDetails(getChartComputeNodesFormulas(workflow.nodes), node);

  // When data are grouped by date column then computeBy are always on top of groupByColumns
  const groupBy = node.groupByColumns[0]?.startsWith('computeBy')
    ? computeData.data
    : node.groupByColumns[0];
  const groupByDetails = node.groupByColumns[0]?.startsWith('computeBy')
    ? computeData.details
    : undefined;

  // When data are additionally segmented (or segmented by date) then segment is on the bottom of groupByColumns
  const reversedGroupByColumns =
    node.groupByColumns.length > 1 &&
    !node.groupByColumns.every((col) => col.startsWith('computeBy'))
      ? [...node.groupByColumns].reverse()
      : [];
  const segmentData = reversedGroupByColumns[0]?.startsWith('computeBy')
    ? computeData.data
    : reversedGroupByColumns[0];
  const segmentDataDetails = reversedGroupByColumns[0]?.startsWith('computeBy')
    ? computeData.details
    : undefined;

  element.props = applyPropsPatch(element.props, {
    content: {
      data: {
        aggregation: node.functionDefs[0].name,
        mainValue: node.functionDefs[0].column,
        alias: node.functionDefs[0].alias,
        multiSeries: [
          { alias: node.functionDefs[0].alias },
          node.functionDefs
            .map((def, index) => {
              if (index === 0) {
                return null;
              }
              const { alias, name, column } = def;
              return {
                alias: alias,
                aggregation: name,
                mainValue: column,
              };
            })
            .filter(Boolean),
        ] as NonNullable<PropsOf<'chartLine'>['content']['data']>['multiSeries'],
        groupBy,
        groupByDetails,
        segmentData,
        segmentDataDetails,
      },
    },
  }) as PropsOf<'chartBar' | 'chartLine' | 'chartColumn' | 'chartDot'>;
}

export function updateElementPropsFromAiWorkflowMutably(
  element: Draft<CanvasElement>,
  workflow: Workflow,
): void {
  if (isElementType(element, 'chartSingle')) {
    updateChartSinglePropsMutably(element, workflow);
  } else if (isElementType(element, 'chartPie') || isElementType(element, 'chartDoughnut')) {
    updateSweetChartPropsMutably(element, workflow);
  } else if (
    isElementType(element, 'chartLine') ||
    isElementType(element, 'chartDot') ||
    isElementType(element, 'chartBar') ||
    isElementType(element, 'chartColumn')
  ) {
    updateChartPropsMutably(element, workflow);
  }
}

/** Populate label if initial */
export function tryApplyLabelMutably(
  draft: Draft<EntityState<CanvasElement>>,
  elementId: CanvasElementId,
  isInitialSelection: boolean,
  dataSourceName?: string,
): void {
  const element = draft.entities[elementId];
  if (!element || !hasProps(element)) {
    return;
  }

  if (
    isInitialSelection &&
    dataSourceName &&
    ELEMENTS_WITH_AUTO_INITIAL_LABEL.includes(element.type) &&
    // don't overwrite when explicitly disabled
    (!areContentPropsWithTitle(element) || element.props.content.title.enabled !== false)
  ) {
    element.props = applyPropsPatch(element.props, {
      content: { title: { enabled: true, label: dataSourceName } },
    }) as typeof element.props;
  }
}
